import React, { useState, useEffect, useCallback } from "react";
import { FaTrash } from "react-icons/fa";
import { useUploadImageMutation } from "../../app/api/console/consoleImagesApiSlice";
import { REGION_CODES, getRegionName } from "../../constants/regions";
import DeleteConfirmationModal from "./DeleteConfirmationModal";


const EditableIssue = ({ issue, onUpdate, onDelete, startEditing = false, forceEditMode = true }) => {
  console.log("EDITABLE ISSUE issue", issue);
  const [isEditing, setIsEditing] = useState(startEditing);
  const [editedIssue, setEditedIssue] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [regionSearch, setRegionSearch] = useState("");
  const [uploadImage] = useUploadImageMutation();
  const [imageUploading, setImageUploading] = useState({});
  // Remove the isExpanded state since we're using edit mode instead

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleImageDrop = useCallback(
    async (event, index, imageField) => {
      event.preventDefault();
      event.stopPropagation();

      const files = event.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (!file.type.match("image.*")) {
          alert("Please drop an image file");
          return;
        }

        // Update uploading state
        setImageUploading((prev) => ({ ...prev, [`${index}-${imageField}`]: true }));

        try {
          // Upload image using the API
          const result = await uploadImage(file).unwrap();
          console.log("Upload success:", result);
          // Update the row with the uploaded image URL
          const updatedRows = [...editedIssue.issue_rows];
          updatedRows[index] = {
            ...updatedRows[index],
            [imageField]: result.url,
          };
          setEditedIssue({
            ...editedIssue,
            issue_rows: updatedRows,
          });
        } catch (error) {
          console.error("Failed to upload image:", error);
          alert(`Failed to upload image: ${error.message || "Unknown error"}`);
        } finally {
          setImageUploading((prev) => ({ ...prev, [`${index}-${imageField}`]: false }));
        }
      }
    },
    [uploadImage, editedIssue]
  );

  const handleFileChange = useCallback(
    async (event, index, imageField) => {
      const file = event.target.files[0];
      if (file) {
        if (!file.type.match("image.*")) {
          alert("Please select an image file");
          return;
        }

        setImageUploading((prev) => ({ ...prev, [`${index}-${imageField}`]: true }));

        try {
          console.log("Uploading file:", file.name, file.type);
          const result = await uploadImage(file).unwrap();
          console.log("Upload success:", result);
          const updatedRows = [...editedIssue.issue_rows];
          updatedRows[index] = {
            ...updatedRows[index],
            [imageField]: result.url,
          };
          setEditedIssue({
            ...editedIssue,
            issue_rows: updatedRows,
          });
        } catch (error) {
          console.error("Failed to upload image:", error);
          alert(`Failed to upload image: ${error.message || "Unknown error"}`);
        } finally {
          setImageUploading((prev) => ({ ...prev, [`${index}-${imageField}`]: false }));
        }
      }
    },
    [uploadImage, editedIssue]
  );

  useEffect(() => {
    if (issue) {
      // split the incoming object so we can spread **without** carrying the wrapper keys
      const { visible_issue_content = {}, created_issue_content = {}, ...topLevel } = issue;

      // order matters: nested wins over top-level when both provide the same key
      setEditedIssue({
        id: issue.id || issue.issue_id,
        ...topLevel,
        ...created_issue_content,
        ...visible_issue_content,
      });

      // If startEditing is true, ensure we're in edit mode
      if (startEditing && !isEditing) {
        setIsEditing(true);
      }
    }
    // `isEditing` is intentionally not a dependency: this effect re-seeds `editedIssue`
    // from the `issue` prop and must not re-run (discarding in-progress edits) when
    // edit mode toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue, startEditing]);

  // Update edit mode when forceEditMode prop changes
  useEffect(() => {
    setIsEditing(forceEditMode || startEditing);
  }, [forceEditMode, startEditing]);

  if (!issue || !editedIssue) return null;

  const handleInputChange = (e) => {
    setEditedIssue({
      ...editedIssue,
      [e.target.name]: e.target.value,
    });
  };

  const handleMultiSelectChange = (field, value) => {
    // Toggle selection in arrays (regions, platforms)
    const currentValues = [...(editedIssue[field] || [])];
    const index = currentValues.indexOf(value);

    if (index >= 0) {
      currentValues.splice(index, 1);
    } else {
      currentValues.push(value);
    }

    setEditedIssue({
      ...editedIssue,
      [field]: currentValues,
    });
  };

  const handleRowChange = (index, key, value) => {
    const updatedRows = [...editedIssue.issue_rows];
    updatedRows[index] = {
      ...updatedRows[index],
      [key]: value,
    };
    setEditedIssue({
      ...editedIssue,
      issue_rows: updatedRows,
    });
  };

  const handleDeleteRow = (index) => {
    const updatedRows = [...editedIssue.issue_rows];
    updatedRows.splice(index, 1);
    setEditedIssue({
      ...editedIssue,
      issue_rows: updatedRows,
    });
  };

  const handleMoveRowUp = (index) => {
    if (index === 0) return;
    const updatedRows = [...editedIssue.issue_rows];
    const temp = updatedRows[index];
    updatedRows[index] = updatedRows[index - 1];
    updatedRows[index - 1] = temp;
    setEditedIssue({
      ...editedIssue,
      issue_rows: updatedRows,
    });
  };

  const handleMoveRowDown = (index) => {
    if (index === editedIssue.issue_rows.length - 1) return;
    const updatedRows = [...editedIssue.issue_rows];
    const temp = updatedRows[index];
    updatedRows[index] = updatedRows[index + 1];
    updatedRows[index + 1] = temp;
    setEditedIssue({
      ...editedIssue,
      issue_rows: updatedRows,
    });
  };

  const handleSave = () => {
    const updatedIssue = {
      ...issue,
      title: editedIssue.title,
      severity: editedIssue.severity,
      status: editedIssue.status,
      regions: editedIssue.regions,
      platforms: editedIssue.platforms,
      description: editedIssue.description,
      shortlink_url: editedIssue.shortlink_url,
      shortlink_title: editedIssue.shortlink_title,
      issue_rows: editedIssue.issue_rows,

      visible_issue_content: {
        ...editedIssue,
        id: undefined,
      },
    };

    console.log("Clicked Save");
    console.log("updatedIssue", updatedIssue);
    onUpdate(updatedIssue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedIssue({ ...issue });
    setIsEditing(false);
    setRegionSearch("");
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(issue.id || issue.issue_id);
    setShowDeleteModal(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const hasChanges = issue && editedIssue && JSON.stringify(issue) !== JSON.stringify(editedIssue);

  const severityColors = {
    high: "bg-red-100 text-red-800",
    medium: "bg-yellow-100 text-yellow-800",
    low: "bg-green-100 text-green-800",
  };

  const statusColors = {
    open: "bg-blue-100 text-blue-800",
    closed: "bg-gray-100 text-gray-800",
    discarded: "bg-red-100 text-red-800",
  };

  // Issue row types mapping for UI
  const rowTypeMap = {
    image: "Image",
    text_before_after: "Text Before/After",
    image_before_after: "Image Before/After",
    text: "Text",
  };

  // Render specific row fields based on row type
  const renderRowFields = (row, index) => {
    switch (row.row_type) {
      case "image":
        return (
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Image</label>
            <div className='mb-2'>
              <div
                onDrop={(e) => handleImageDrop(e, index, "image_url")}
                onDragOver={handleDragOver}
                className='border-2 border-dashed border-gray-300 rounded-md p-4 text-center cursor-pointer hover:bg-gray-50 relative'>
                {row.image_url ? (
                  <div className='relative'>
                    <img src={row.image_url} alt='Preview' className='mx-auto max-h-48 object-contain' />
                    <div className='absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black bg-opacity-30 transition-opacity'>
                      <p className='text-white text-sm'>Drop new image to replace</p>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <p className='text-sm text-gray-500'>
                      {imageUploading[`${index}-image_url`]
                        ? "Uploading..."
                        : "Drag & drop an image here, or click to select"}
                    </p>
                    <input
                      type='file'
                      accept='image/*'
                      onChange={(e) => handleFileChange(e, index, "image_url")}
                      className='absolute inset-0 opacity-0 w-full cursor-pointer'
                      disabled={imageUploading[`${index}-image_url`]}
                    />
                  </div>
                )}
              </div>
            </div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Image URL</label>
            <input
              type='text'
              value={row.image_url || ""}
              onChange={(e) => handleRowChange(index, "image_url", e.target.value)}
              className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
            />
          </div>
        );

      case "text":
        return (
          <div className='space-y-2'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Title (Optional)</label>
              <input
                type='text'
                value={row.title || ""}
                onChange={(e) => handleRowChange(index, "title", e.target.value)}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Content</label>
              <textarea
                value={row.content || ""}
                onChange={(e) => handleRowChange(index, "content", e.target.value)}
                rows={3}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
              />
            </div>
          </div>
        );

      case "text_before_after":
        return (
          <div className='space-y-2'>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>Before Title</label>
                <input
                  type='text'
                  value={row.before_title || ""}
                  onChange={(e) => handleRowChange(index, "before_title", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>After Title</label>
                <input
                  type='text'
                  value={row.after_title || ""}
                  onChange={(e) => handleRowChange(index, "after_title", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>Before Content</label>
                <textarea
                  value={row.before_content || ""}
                  onChange={(e) => handleRowChange(index, "before_content", e.target.value)}
                  rows={3}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>After Content</label>
                <textarea
                  value={row.after_content || ""}
                  onChange={(e) => handleRowChange(index, "after_content", e.target.value)}
                  rows={3}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
            </div>
          </div>
        );

      case "image_before_after":
        return (
          <div className='space-y-2'>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>Before Title</label>
                <input
                  type='text'
                  value={row.before_title || ""}
                  onChange={(e) => handleRowChange(index, "before_title", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>After Title</label>
                <input
                  type='text'
                  value={row.after_title || ""}
                  onChange={(e) => handleRowChange(index, "after_title", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>Before Image</label>
                <div
                  onDrop={(e) => handleImageDrop(e, index, "before_image_url")}
                  onDragOver={handleDragOver}
                  className='mb-2 border-2 border-dashed border-gray-300 rounded-md p-4 text-center cursor-pointer hover:bg-gray-50 relative'>
                  {row.before_image_url ? (
                    <div className='relative'>
                      <img
                        src={row.before_image_url}
                        alt='Before Preview'
                        className='mx-auto max-h-48 object-contain'
                      />
                      <div className='absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black bg-opacity-30 transition-opacity'>
                        <p className='text-white text-sm'>Drop new image to replace</p>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <p className='text-sm text-gray-500'>
                        {imageUploading[`${index}-before_image_url`]
                          ? "Uploading..."
                          : "Drag & drop an image here, or click to select"}
                      </p>
                      <input
                        type='file'
                        accept='image/*'
                        onChange={(e) => handleFileChange(e, index, "before_image_url")}
                        className='absolute inset-0 opacity-0 w-full cursor-pointer'
                        disabled={imageUploading[`${index}-before_image_url`]}
                      />
                    </div>
                  )}
                </div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>Before Image URL</label>
                <input
                  type='text'
                  value={row.before_image_url || ""}
                  onChange={(e) => handleRowChange(index, "before_image_url", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>After Image</label>
                <div
                  onDrop={(e) => handleImageDrop(e, index, "after_image_url")}
                  onDragOver={handleDragOver}
                  className='mb-2 border-2 border-dashed border-gray-300 rounded-md p-4 text-center cursor-pointer hover:bg-gray-50 relative'>
                  {row.after_image_url ? (
                    <div className='relative'>
                      <img src={row.after_image_url} alt='After Preview' className='mx-auto max-h-48 object-contain' />
                      <div className='absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black bg-opacity-30 transition-opacity'>
                        <p className='text-white text-sm'>Drop new image to replace</p>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <p className='text-sm text-gray-500'>
                        {imageUploading[`${index}-after_image_url`]
                          ? "Uploading..."
                          : "Drag & drop an image here, or click to select"}
                      </p>
                      <input
                        type='file'
                        accept='image/*'
                        onChange={(e) => handleFileChange(e, index, "after_image_url")}
                        className='absolute inset-0 opacity-0 w-full cursor-pointer'
                        disabled={imageUploading[`${index}-after_image_url`]}
                      />
                    </div>
                  )}
                </div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>After Image URL</label>
                <input
                  type='text'
                  value={row.after_image_url || ""}
                  onChange={(e) => handleRowChange(index, "after_image_url", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>Before Shortlink URL (Optional)</label>
                <input
                  type='text'
                  value={row.before_shortlink_url || ""}
                  onChange={(e) => handleRowChange(index, "before_shortlink_url", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>After Shortlink URL (Optional)</label>
                <input
                  type='text'
                  value={row.after_shortlink_url || ""}
                  onChange={(e) => handleRowChange(index, "after_shortlink_url", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Before Shortlink Title (Optional)
                </label>
                <input
                  type='text'
                  value={row.before_shortlink_title || ""}
                  onChange={(e) => handleRowChange(index, "before_shortlink_title", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>After Shortlink Title (Optional)</label>
                <input
                  type='text'
                  value={row.after_shortlink_title || ""}
                  onChange={(e) => handleRowChange(index, "after_shortlink_title", e.target.value)}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render issue rows in view mode
  const renderViewModeRows = (rows) => {
    if (!rows || rows.length === 0) return null;

    return (
      <div className='space-y-4'>
        {rows.map((row, index) => {
          switch (row.row_type) {
            case "image":
              return (
                <div key={index} className='border border-gray-200 rounded-lg p-4'>
                  <div className='text-sm font-medium text-gray-700 mb-2'>Image</div>
                  {row.image_url && (
                    <img src={row.image_url} alt='Issue screenshot' className='max-w-full h-auto rounded-md' />
                  )}
                </div>
              );

            case "text":
              return (
                <div key={index} className='border border-gray-200 rounded-lg p-4'>
                  {row.title && <div className='text-sm font-medium text-gray-700 mb-2'>{row.title}</div>}
                  {row.content && <div className='text-sm text-gray-600'>{row.content}</div>}
                </div>
              );

            case "text_before_after":
              return (
                <div key={index} className='border border-gray-200 rounded-lg p-4'>
                  <div className='text-sm font-medium text-gray-700 mb-3'>Text Before/After</div>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      {row.before_title && <div className='text-sm font-medium text-gray-600 mb-1'>Before: {row.before_title}</div>}
                      {row.before_content && <div className='text-sm text-gray-600 bg-red-50 p-3 rounded'>{row.before_content}</div>}
                    </div>
                    <div>
                      {row.after_title && <div className='text-sm font-medium text-gray-600 mb-1'>After: {row.after_title}</div>}
                      {row.after_content && <div className='text-sm text-gray-600 bg-green-50 p-3 rounded'>{row.after_content}</div>}
                    </div>
                  </div>
                </div>
              );

            case "image_before_after":
              return (
                <div key={index} className='border border-gray-200 rounded-lg p-4'>
                  <div className='text-sm font-medium text-gray-700 mb-3'>Image Before/After</div>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      {row.before_title && <div className='text-sm font-medium text-gray-600 mb-2'>Before: {row.before_title}</div>}
                      {row.before_image_url && (
                        <img src={row.before_image_url} alt='Before screenshot' className='max-w-full h-auto rounded-md border-2 border-red-200' />
                      )}
                      {row.before_shortlink_url && (
                        <a href={row.before_shortlink_url} target='_blank' rel='noopener noreferrer' className='text-xs text-blue-600 hover:underline block mt-2'>
                          {row.before_shortlink_title || 'View Before'}
                        </a>
                      )}
                    </div>
                    <div>
                      {row.after_title && <div className='text-sm font-medium text-gray-600 mb-2'>After: {row.after_title}</div>}
                      {row.after_image_url && (
                        <img src={row.after_image_url} alt='After screenshot' className='max-w-full h-auto rounded-md border-2 border-green-200' />
                      )}
                      {row.after_shortlink_url && (
                        <a href={row.after_shortlink_url} target='_blank' rel='noopener noreferrer' className='text-xs text-blue-600 hover:underline block mt-2'>
                          {row.after_shortlink_title || 'View After'}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );

            default:
              return null;
          }
        })}
      </div>
    );
  };

  return (
    <div className='bg-white shadow rounded-lg p-4 mb-4 relative'>
      {/* Delete button in top right corner */}
      <button
        onClick={handleDeleteClick}
        className='absolute top-3 right-3 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors'
        title='Delete Issue'
      >
        <FaTrash className='w-4 h-4' />
      </button>

      {isEditing ? (
        <div className='space-y-4 pr-12'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Title</label>
            <input
              type='text'
              name='title'
              value={editedIssue.title || ""}
              onChange={handleInputChange}
              className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
            />
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>ID</label>
              <input
                type='text'
                name='id'
                value={editedIssue.id || ""}
                disabled
                className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm'
              />
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Severity</label>
              <select
                name='severity'
                value={editedIssue.severity || "medium"}
                onChange={handleInputChange}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'>
                <option value='high'>High</option>
                <option value='medium'>Medium</option>
                <option value='low'>Low</option>
              </select>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Status</label>
              <select
                name='status'
                value={editedIssue.status || "open"}
                onChange={handleInputChange}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'>
                <option value='open'>Open</option>
                <option value='closed'>Closed</option>
                <option value='discarded'>Discarded</option>
              </select>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Regions</label>
              <div className='border border-gray-300 rounded-md overflow-hidden'>
                <div className='p-2 border-b'>
                  <input
                    type='text'
                    placeholder='Search regions...'
                    value={regionSearch}
                    onChange={(e) => setRegionSearch(e.target.value)}
                    className='w-full px-2 py-1 text-sm border border-gray-300 rounded-md'
                  />
                </div>

                {(editedIssue.regions || []).length > 0 && (
                  <div className='px-2 py-1 bg-blue-50 border-b border-gray-300'>
                    <div className='text-xs font-medium text-gray-500 mb-1'>
                      Selected ({(editedIssue.regions || []).length})
                    </div>
                    <div className='flex flex-wrap gap-1'>
                      {(editedIssue.regions || []).map((region) => (
                        <span
                          key={`selected-${region}`}
                          className='inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800'>
                          {region}
                          <button
                            onClick={() => handleMultiSelectChange("regions", region)}
                            className='ml-1 text-blue-500 hover:text-blue-700'>
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className='max-h-32 overflow-y-auto p-2'>
                  {REGION_CODES.filter((region) => region.toLowerCase().includes(regionSearch.toLowerCase())).map(
                    (region) => (
                      <div key={region} className='flex items-center py-0.5'>
                        <input
                          type='checkbox'
                          id={`region-${region}`}
                          checked={(editedIssue.regions || []).includes(region)}
                          onChange={() => handleMultiSelectChange("regions", region)}
                          className='mr-2'
                        />
                        <label htmlFor={`region-${region}`} className='text-sm'>
                          {region} - {getRegionName(region)}
                        </label>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Platforms</label>
              <div className='space-y-1 max-h-32 overflow-y-auto p-2 border border-gray-300 rounded-md'>
                {["mobile", "desktop"].map((platform) => (
                  <div key={platform} className='flex items-center'>
                    <input
                      type='checkbox'
                      id={`platform-${platform}`}
                      checked={(editedIssue.platforms || []).includes(platform)}
                      onChange={() => handleMultiSelectChange("platforms", platform)}
                      className='mr-2'
                    />
                    <label htmlFor={`platform-${platform}`} className='text-sm capitalize'>
                      {platform}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Description</label>
            <textarea
              name='description'
              value={editedIssue.description || ""}
              onChange={handleInputChange}
              rows={3}
              className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Shortlink URL</label>
              <input
                type='text'
                name='shortlink_url'
                value={editedIssue.shortlink_url || ""}
                onChange={handleInputChange}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Shortlink Title</label>
              <input
                type='text'
                name='shortlink_title'
                value={editedIssue.shortlink_title || ""}
                onChange={handleInputChange}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
              />
            </div>
          </div>

          <div className='border-t pt-4'>
            <h4 className='text-base font-medium mb-2'>Issue Rows</h4>
            {(editedIssue.issue_rows || []).map((row, index) => (
              <div key={index} className='mb-4 border p-3 rounded-lg'>
                <div className='flex justify-between items-center mb-2'>
                  <div className='flex items-center'>
                    <span className='text-sm font-medium'>
                      Row {index + 1}: {rowTypeMap[row.row_type]}
                    </span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <button
                      type='button'
                      onClick={() => handleMoveRowUp(index)}
                      disabled={index === 0}
                      className={`p-1 rounded text-gray-500 hover:bg-gray-100 ${index === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                      title='Move up'>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      type='button'
                      onClick={() => handleMoveRowDown(index)}
                      disabled={index === editedIssue.issue_rows.length - 1}
                      className={`p-1 rounded text-gray-500 hover:bg-gray-100 ${index === editedIssue.issue_rows.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                      title='Move down'>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <select
                      value={row.row_type}
                      onChange={(e) => handleRowChange(index, "row_type", e.target.value)}
                      className='text-xs px-2 py-1 border border-gray-300 rounded-md'>
                      <option value='image'>Image</option>
                      <option value='text'>Text</option>
                      <option value='text_before_after'>Text Before/After</option>
                      <option value='image_before_after'>Image Before/After</option>
                    </select>
                    <button
                      type='button'
                      onClick={() => handleDeleteRow(index)}
                      className='p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-red-600'
                      title='Delete row'>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                {renderRowFields(row, index)}
              </div>
            ))}
            <button
              onClick={() => {
                const newRow = { row_type: "text", content: "" };
                setEditedIssue({
                  ...editedIssue,
                  issue_rows: [...(editedIssue.issue_rows || []), newRow],
                });
              }}
              className='text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-md'>
              + Add Row
            </button>
          </div>

          <div className='flex justify-end space-x-2 pt-2'>
            <button
              onClick={handleCancel}
              className='px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50'>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={`px-3 py-1.5 text-sm text-white rounded-md ${
                hasChanges ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"
              }`}>
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className='pr-12'>
          <div className='flex justify-between items-start'>
            <div>
              <h3 className='text-base font-medium text-gray-900'>{issue.title}</h3>
              <div className='flex items-center space-x-2 mt-1'>
                <span className='text-xs text-gray-500'>#{issue.id}</span>
              </div>
              <div className='flex space-x-2 mt-1'>
                {issue.severity && (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      severityColors[issue.severity] || "bg-gray-100 text-gray-800"
                    }`}>
                    {issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)}
                  </span>
                )}
                {issue.status && (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      statusColors[issue.status] || "bg-gray-100 text-gray-800"
                    }`}>
                    {issue.status.charAt(0).toUpperCase() + issue.status.slice(1)}
                  </span>
                )}
              </div>
            </div>

            <div className='flex space-x-2'>
              <button onClick={() => setIsEditing(true)} className='text-xs text-blue-600 hover:text-blue-800'>
                Edit
              </button>
            </div>
          </div>

          {issue.description && <p className='mt-2 text-sm text-gray-500'>{issue.description}</p>}

          <div className='mt-3 text-xs text-gray-500'>
            <div className='flex space-x-4'>
              <div>
                <span className='font-medium'>Regions:</span> {issue.regions?.join(", ") || "None"}
              </div>
              <div>
                <span className='font-medium'>Platforms:</span> {issue.platforms?.join(", ") || "None"}
              </div>
            </div>
            {issue.shortlink_url && (
              <div className='mt-2'>
                <span className='font-medium'>Shortlink:</span>{" "}
                <a
                  href={issue.shortlink_url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-600 hover:underline'>
                  {issue.shortlink_title || issue.shortlink_url}
                </a>
              </div>
            )}
          </div>

          {issue.issue_rows?.length > 0 && (
            <div className='mt-4 border-t pt-3'>
              <h4 className='text-sm font-medium text-gray-900 mb-3'>Content Rows ({issue.issue_rows.length})</h4>
              {renderViewModeRows(issue.issue_rows)}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Issue"
        message={`Are you sure you want to delete "${issue.title || 'this issue'}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default EditableIssue;
